import advertisements from './advertisements';
import template from '../helpers/template';
import log from '../helpers/log';
import reveal from '../helpers/reveal';
import { getParameterByName } from '../helpers/utilities';

interface ICollectionGridConfig {
  productsPerPage: number,
  currentPage: number,
  totalProducts: number,
  htmlElement: HTMLElement,
  selectors: ICollectionGridSelectors,
  loadMoreBtn: ICollectionGridLoadMoreBtn,
  productTiles: ICollectionGridProductTiles,
  items: ICollectionGridItems,
  scrollPosition: number,
  advertisements: any
}

interface ICollectionGridItems {
  nodeList: NodeList
}

interface ICollectionGridProductTiles {
  nodeList: NodeList
}

interface ICollectionGridLoadMoreBtn {
  htmlInputElement: HTMLInputElement
}

interface ICollectionGridSelectors {
  main: string,
  productTile: string,
  advertisement: string,
  loadMoreBtn: string,
  paginationCurrentlyDisplayed: string
}

class Collection {

  public config:ICollectionGridConfig;

  constructor() {
    this.config = {
      htmlElement: null,
      scrollPosition: 0,
      productsPerPage: window.theme.collection.productsPerPage,
      currentPage: null,
      totalProducts: null,
      items: {
        nodeList: null
      },
      productTiles: {
        nodeList: null,
      },
      advertisements: null,
      loadMoreBtn: {
        htmlInputElement: null
      },
      selectors: {
        main: '[data-collection-grid]',
        productTile: '[data-product-tile]',
        advertisement: '[data-advertisement]',
        loadMoreBtn: '[data-collection-grid-load-more-btn]',
        paginationCurrentlyDisplayed: '[data-pagination-currently-displayed]'
      }
    }
  }

  init() {
    document.querySelectorAll(this.config.selectors.main).forEach((htmlElement: HTMLElement) => {
      this.load(htmlElement);
    });
  }

  load(htmlElement: HTMLElement) {
    this.config.htmlElement = htmlElement;
    this.config.currentPage = window.theme.collection.pagination.currentPage;
    this.config.totalProducts = window.theme.collection.pagination.totalProducts;
    this.config.items.nodeList = this.config.htmlElement.querySelectorAll(`${ this.config.selectors.productTile }, ${ this.config.selectors.advertisement }`);
    this.config.productTiles.nodeList = this.config.htmlElement.querySelectorAll(this.config.selectors.productTile);
    this.config.loadMoreBtn.htmlInputElement = document.querySelector(this.config.selectors.loadMoreBtn);
    this.config.advertisements = window.theme.collection.advertisements;
    if (this.config.advertisements) advertisements.init(window.theme.collection.advertisements, this.config.currentPage);
    this.tidy();
    this.updatePagination();
    this.updateLoadMoreBtn();
    this.updateHeight();
    if (this.config.loadMoreBtn.htmlInputElement) this.config.loadMoreBtn.htmlInputElement.addEventListener('click', this.handleClickLoadMore.bind(this));
  }

  tidy() {
    this.config.htmlElement.querySelectorAll(`${ this.config.selectors.productTile }, ${ this.config.selectors.advertisement }`).forEach((item: HTMLElement, index: number) => {
      item.classList.remove('hide');
      if (index >= this.config.productsPerPage * this.config.currentPage) item.classList.add('hide');
    });
  }

  updateLoadMoreBtn() {
    if (this.config.productTiles.nodeList.length == this.config.totalProducts || this.config.productTiles.nodeList.length === 0) {
      this.config.loadMoreBtn.htmlInputElement.closest('.row').classList.add('hide');
    }
  }

  updatePagination() {
    const shownProducts = Array.from(this.config.productTiles.nodeList).filter((node:HTMLElement) => !node.classList.contains('hide'));
    document.querySelectorAll(this.config.selectors.paginationCurrentlyDisplayed).forEach(paginationCurrentlyDisplayedHtmlElement => {
      paginationCurrentlyDisplayedHtmlElement.textContent = String(shownProducts.length);
    });
  }

  async update() {
    this.disableLoadMoreBtn();
    try {
      this.config.scrollPosition = window.scrollY;
      await template.getAndAppendHTML({
        node: this.config.htmlElement, 
        content: '[data-collection-grid-page]', 
        url: window.location.pathname + `?page=${ this.config.currentPage + 1 }`,
        view: 'product-tiles',
        sortBy: getParameterByName('sort_by')
      });
      this.config.items.nodeList = this.config.htmlElement.querySelectorAll(`${ this.config.selectors.productTile }, ${ this.config.selectors.advertisement }`);
      this.config.productTiles.nodeList = this.config.htmlElement.querySelectorAll(this.config.selectors.productTile);
      this.config.currentPage += 1;
      if (this.config.advertisements) advertisements.update(this.config.currentPage);
      this.tidy();
      this.updatePagination();
      this.updateLoadMoreBtn();
      this.enableLoadMoreBtn();
      this.updateHeight();
    }
    catch(error) {
      log.error(error);
    }
  }

  updateHeight() {
    const imgs = this.config.htmlElement.querySelectorAll('img');
    imgs.forEach(img => img.addEventListener('load', () => {
      window.scrollTo(0, this.config.scrollPosition);
    }))
  }

  disableLoadMoreBtn() {
    this.config.loadMoreBtn.htmlInputElement.disabled = true;
    this.config.loadMoreBtn.htmlInputElement.classList.add('btn--loading');
  }

  enableLoadMoreBtn() {
    this.config.loadMoreBtn.htmlInputElement.disabled = false;
    this.config.loadMoreBtn.htmlInputElement.classList.remove('btn--loading');
  }

  handleClickLoadMore(event:Event) {
    event.preventDefault();
    if (!this.config.loadMoreBtn.htmlInputElement.disabled) this.update();
  }
}

export default new Collection();